<template>
  <drawer-form-layout
    class="check-point-edit-form"
    :model="model"
    :rules="rules"
    @finish="onSubmit"
    @validate="errors.onValidate"
  >
    <template #header>Редактировать контрольную точку</template>
    <template #instruction>
      <p>
        Внесите изменения и сохраните их. Если изменений нет, просто закройте это окно.
      </p>  
    </template>
    <template #default>
      <input-item
        v-model="model.description"
        :disabled="isLocked"
        :errors="errors"
        name="description"
        placeholder="Сформулируйте результат"
      >
        <template #suffix>
          <icon-delete 
            v-if="model.description && !isLocked"
            @click="removeCheckpointDescription"
          />
        </template>
      </input-item>

      <check-point-date-layout>
        <template #date>
          <date-picker-item
            v-model="model.date"
            :disabled="isLocked || model.noMatter"
            :errors="errors"
            name="date"
            placeholder="Дата выполнения"
          >
            <template #suffixIcon></template>
          </date-picker-item>
        </template>
        <template #no-matter>
          <checkbox-item
            v-model="model.noMatter"
            :disabled="isLocked"
            :errors="errors"
            name="noMatter"
            @change="onNoMatterChange(checkPoint)"
          >
            Неважно
          </checkbox-item>
        </template>
      </check-point-date-layout>

      <h3>Отметить результат выполнения</h3>

      <checkbox-item
        v-model="model.isCompleted"
        :errors="errors"
        name="isCompleted"
        @change="handleCompletedClick"
      >
        Выполнено
      </checkbox-item>

      <check-point-competence-form
        v-if="showCompetences"
        ref="competenciesRef"
        :checkpoint="checkPoint"
        :is-completed="model.isCompleted"
        :disabled="isLocked"
        @update="() => (dataChanged = true)"
        @saved="(result) => onChildSaved(result, COMPONENT_ID_COMPETENCIES)"
      />

      <check-point-quality-form
        v-if="showQualities"
        ref="qualitiesRef"
        :checkpoint="checkPoint"
        :is-completed="model.isCompleted"
        :disabled="isLocked"
        @update="() => (dataChanged = true)"
        @saved="(result) => onChildSaved(result, COMPONENT_ID_QUANTITIES)"
      />
      
      <template v-if="volunteeringEnabled">
        <h3>Волонтёрство</h3>

        <period-item
          v-for="(_, index) in model.periods"
          :key="`period-${index}`"
          v-model="model.periods[index].range"
          :disabled="isLocked"
          :disabled-date="(currentDate) => disabledDatePeriod(index, currentDate)"
          :errors="errors"
          :name="['periods', index]"
          :placeholder="['Начало', 'Окончание']"
          :rules="nestedRules.periods.fields[index]"
          @calendar-change="onCalendarChange"
          @open-change="onOpenChange"
          @remove="() => removePeriod(index)"
        />

        <adding-control v-if="!isLocked" text="Добавить период" @click="addPeriod"/>
      </template>

      <textarea-item
        v-model="model.comment"
        :disabled="isLocked || disabled"
        :errors="errors"
        :max-length="10000"
        name="comment"
        placeholder="Напишите комментарий при необходимости"
      />
    </template>
    <template #controls>
      <!-- TODO: упростить верстку кнопок -->
      <a-row :align="'bottom'" justify="space-between" class="check-point-edit-form__buttons">
        <a-col>
          <div v-if="showDeleteButton" class="buttons__delete">
            <a @click="(event) => $emit('delete-action', event)">Удалить</a>
          </div>
        </a-col>
        <a-col>
          <a-form-item class="buttons__save">
            <c-button-primary
              html-type="submit"
              :loading="isLoading"
              :disabled="isLocked && !dataChanged && USER_ROLE_LEADER !== route.meta.role"
            >
              Сохранить
            </c-button-primary>
          </a-form-item>
        </a-col>
      </a-row>
    </template>
  </drawer-form-layout>
</template>

<script setup>
  import { computed, onMounted, reactive, ref } from 'vue';
  import { useApi } from '@/api/use-api';
  import ErrorsHandler from '@/components/form/errors-handler';
  import CButtonPrimary from '@/components/UI/CButtonPrimary';
  import dayjs from 'dayjs';
  import { TEXT_LENGTH_ERROR } from '@/constants/error';
  import { useRoute } from 'vue-router';
  import CheckPointCompetenceForm from '@/components/projects/management/CheckPoints/CheckPointCompetenceForm.vue';
  import { USER_ROLE_LEADER, USER_ROLE_PERFORMER } from '@/api/models/UserRole';
  import { useStore } from 'vuex';
  import CheckPointQualityForm from '@/components/projects/management/CheckPoints/CheckPointQualityForm.vue';
  import { useFormSyncSubmit } from '@/composables/useFormSyncSubmit';
  import IconDelete from '@/components/form/components/IconDelete.vue';
  import InputItem from '@/components/form/components/InputItem.vue';
  import DatePickerItem from '@/components/form/components/DatePickerItem.vue';
  import CheckboxItem from '@/components/form/components/CheckboxItem.vue';
  import TextareaItem from '@/components/form/components/TextareaItem.vue';
  import CheckPointDateLayout from './CheckPointDateLayout.vue';
  import DrawerFormLayout from '@/components/form/layouts/DrawerFormLayout.vue';
  import AddingControl from '@/components/controls/components/AddingControl.vue';
  import { Period } from './models/period';
  import PeriodItem from './PeriodItem.vue';
  import { PROJECT_TASK_ROLE_VOLUNTEER } from '@/constants/project-task-role';

  const COMPONENT_ID_COMPETENCIES = 'competencies';
  const COMPONENT_ID_QUANTITIES = 'quantities';

  const props = defineProps({
    checkPoint: Object,
    disabled: {
      type: Boolean,
      default: false,
    },
  });

  const emit = defineEmits(['updated', 'delete-action']);

  const api = useApi();
  const route = useRoute();
  const store = useStore();
  const { registerParentCallback, registerChild, childSaved, submit } = useFormSyncSubmit();
  const competenciesRef = ref(null);
  const qualitiesRef = ref(null);
  const isLoading = ref(false);
  const isChanged = ref(false);
  const dataChanged = ref(false);

  // TODO: вынести в адаптер логику преобразования данных из API в данные формы
  const model = reactive({
    description: props.checkPoint.description,
    date: props.checkPoint.date ? dayjs(props.checkPoint.date) : null,
    comment: props.checkPoint.comment,
    noMatter: props.checkPoint.date === null,
    isCompleted: !!props.checkPoint.dateCompleted,
    isMy: props.checkPoint.isMy,
    periods: [],
  });

  // TODO: competences переименовать в competencies (от competency - более точный термин)
  const showCompetences = computed(() => {
    return store.getters.enableCompetencies &&
      (model.isMy || USER_ROLE_LEADER === route.meta.role);
  });

  const showQualities = computed(() => {
    return store.getters.enableQualities &&
      model.isMy &&
      route.meta.role === USER_ROLE_PERFORMER &&
      store.state.person.isStudent;
  });

  /** Блокируем форму кроме чекбокса 'выполнено'> */
  const isLocked = computed(() => {
    return !isChanged.value && model.isCompleted;
  });

  

  /** Не показываем кнопку "удалить" при вызове из формы списка задач для исполнителя */
  const showDeleteButton = computed(() => {
    // TODO: Исполнитель не может удалить контрольную точку, если зайдет из меню "Мои задачи",
    // но может это сделать, если зайдет из меню "Мои проекты".
    // Нужно уточнить правильную логику работы этого ограничения.
    return route.name !== 'performer-tasks';
  });

  const rules = {
    // TODO: переименовать в result, чтобы были одинаковые названия с компонентом TaskForm
    description: [
      {
        type: 'string',
        required: true,
        min: 2,
        max: 5000,
        message: TEXT_LENGTH_ERROR,
        trigger: 'change',
      },
    ],
    date: [
      {
        type: 'any',
        required: !model.noMatter,
        message: 'Необходимо указать дату выполнения',
        trigger: ['change', 'blur'],
      },
    ],
    noMatter: {
      type: 'boolean',
    },
    isCompleted: {
      type: 'boolean',
    },
    comment: [
      {
        type: 'string',
        required: false,
        min: 2,
        max: 10000,
        message: 'Введите комментарий не менее 2, но не более 10000 символов',
        trigger: 'change',
      },
    ],
  };

  const nestedRules = reactive({
    periods: {
      type: 'array',
      fields: model.periods.map(() => Period.rulesBlock()),
    },
  });

  const errors = new ErrorsHandler({
    description: '',
    date: '',
    comment: '',
  });

  // Сохранение даты начала периода при клике в календаре. 
  // Используется для фильтрации выбора даты окончания периода.
  const periodStart = ref();

  // массив идентификаторов удаленных периодов
  const removedPeriods = ref([]);

  const addPeriod = () => {
    model.periods.push(Period.generate());
    nestedRules.periods.fields.push(Period.rulesBlock());
  };

  const removePeriod = (index) => {
    const removed = model.periods.splice(index, 1);
    nestedRules.periods.fields.splice(index, 1);

    // вариант цикла for-in не работает из-за реактивности
    for (let i = 0; i < removed.length; i++) {
      if (null !== removed[i].id) {
        removedPeriods.value.push(removed[i].id);
      }
    }
  };

  const onCalendarChange = (dates, dateStrings, info) => {
    if ('start' === info.range) {
      periodStart.value = dates[0];
    } else {
      periodStart.value = undefined;
    }
  };

  /**
   * @param {Boolean} status
   */
  const onOpenChange = (status) => {
    if (!status) {
      periodStart.value = undefined;
    }
  };

  const disabledDatePeriod = (periodIndex, currentDate) => {
    for (let i = 0; i < model.periods.length; i++) {
      if (
        periodIndex === i ||
        undefined === model.periods[i].range[0] ||
        undefined === model.periods[i].range[1] ||
        undefined !== periodStart.value && periodStart.value.isAfter(model.periods[i].range[1], 'day')
      ) {
        continue;
      }

      // ограничиваем выбор даты окончания периода, у которого выбрана начальная дата
      if (undefined !== periodStart.value) {
        if (currentDate.isSameOrAfter(model.periods[i].range[0], 'day')) {
          return true;
        } else {
          continue;
        }
      }

      // убираем даты, которые уже выбраны в других периодах
      if (currentDate.isBetween(model.periods[i].range[0], model.periods[i].range[1], 'day', '[]')) {
        return true;
      }
    }

    return false;
  };

  const volunteeringEnabled = ref(false);

  const loadVolunteeringEnabled = async () => {
    try {
      let response = await api.projectTask.view(props.checkPoint.taskId);

      if (null === response.projectTask.projectTaskRoleId) {
        return;
      }

      response = await api.projectTaskRole.view(response.projectTask.projectTaskRoleId);

      volunteeringEnabled.value = PROJECT_TASK_ROLE_VOLUNTEER === response.projectTaskRole.slug;
    } catch (error) {
      // TODO: добавить обработку ошибок
      console.log(error);
    }
  };

  const loadPeriods = async () => {
    try {
      const response = await api.volunteeringPeriod.index({
        projectCheckpointId: props.checkPoint.id,
      });

      // TODO 
      // API выдает за один раз не более 20 периодов.
      // Нужно ли вводить ограничение на их добавление или нужно подгружать
      // дополнительные периоды, если их будет больше 20?
      // Если подгружать, то, возможно, стоит поменять способ отображения периодов
      // в интерфейсе, чтобы пользователю не нужно было листать портянку из периодов.
      model.periods = response.periods.items.map((period) => ({
        id: period.id,
        range: [
          dayjs(period.startedAt),
          dayjs(period.endedAt),
        ],
      }));
    } catch (error) {
      // TODO: добавить обработку ошибок
      console.log(error);
    }
  };

  const saveVolunteeringCheckpoint = async () => {
    try {
      const response = await api.volunteeringCheckpoint.create({
        projectCheckpointId: props.checkPoint.id,
      });

      return response.id;
    } catch (error) {
      // TODO: добавить обработку ошибок
      console.log(error);
    }
  };

  const saveVolunteeringPeriods = async (volunteeringCheckpointId) => {
    try {
      for (let i = 0; i < model.periods.length; i++) {
        if (undefined === model.periods[i].range[0] || undefined === model.periods[i].range[1]) {
          continue;
        }

        let requestData = {
          volunteeringCheckpointId,
          startedAt: model.periods[i].range[0].format('YYYY-MM-DD'),
          endedAt: model.periods[i].range[1].format('YYYY-MM-DD'),
        };

        if (null === model.periods[i].id) {
          let response = await api.volunteeringPeriod.create(requestData);
          model.periods[i].id = response.id;
        } else {
          await api.volunteeringPeriod.update(model.periods[i].id, requestData);
        }
      }
    } catch (error) {
      // TODO: добавить обработку ошибок
      console.log(error);
    }
  };

  const removeVolunteeringPeriods = async () => {
    try {
      for (let i = 0; i < removedPeriods.value.length; i++) {
        await api.volunteeringPeriod.remove(removedPeriods.value[i]);
      }
    } catch (error) {
      // TODO: добавить обработку ошибок
      console.log(error);
    }
  };

  // TODO: подумать над тем, как можно упростить этот процесс и избавиться от
  // defineExpose() в дочерних компонентах. Цель: использовать традиционный поток
  // данных от родителя к дочерним компонентам, который принят во Vue по умолчанию.
  // defineExpose() нарушает этот принцип.
  onMounted(async () => {
    registerParentCallback(onFinish);
    registerChild({
      id: COMPONENT_ID_COMPETENCIES,
      ref: competenciesRef.value,
      enabled: store.getters.enableCompetencies,
    });
    registerChild({
      id: COMPONENT_ID_QUANTITIES,
      ref: qualitiesRef.value,
      enabled: store.getters.enableQualities,
    });

    await loadVolunteeringEnabled();

    if (volunteeringEnabled.value) {
      await loadPeriods();
    }
  });

  const onSubmit = async () => {
    model.isLoading = true;
    await submit();
  };

  const onChildSaved = (result, childId) => {
    if (result) {
      childSaved(childId);
    } else {
      model.isLoading = false;
    }
  };

  const onFinish = async () => {
    try {
      await api.projectManagementPage.saveCheckpoint({
        id: props.checkPoint.id,
        taskId: props.checkPoint.taskId,
        description: model.description,
        date: model.noMatter ? null : dayjs(model.date).format('YYYY-MM-DD'),
        comment: model.comment,
        isCompleted: model.isCompleted,
      });

      if (volunteeringEnabled.value) {
        const volunteeringCheckpointId = await saveVolunteeringCheckpoint();
        await saveVolunteeringPeriods(volunteeringCheckpointId);
        await removeVolunteeringPeriods();
      }

      emit('updated');
    } catch (error) {
      errors.handleApiErrors(error);
    } finally {
      isLoading.value = false;
    }
  };

  const removeCheckpointDescription = () => {
    model.description = '';
  };

  const onNoMatterChange = (checkPoint) => {
    if (checkPoint.noMatter) {
      checkPoint.date = null;
    }
  };

  const handleCompletedClick = () => {
    isChanged.value = true;
  };
</script>

<style lang="scss" scoped>
  @use "@/assets/styles/_colors.scss" as *;

  :deep(.ant-input) {
    font-family: 'Nunito Sans', sans-serif;
    &[disabled] {
      color: $color-text-primary;
    }
  }

  :deep(.ant-picker-input > input) {
    &[disabled] {
      color: $color-text-primary;
    }
  }

  .check-point-edit-form {
    &__buttons {
      margin-top: 50px;

      .buttons {
        &__delete {
          a {
            border-bottom: 1px solid;
            color: $color-text-accent-red;
            font: 400 14px/20px 'Nunito Sans';
            text-decoration: none;
          }
        }

        &__save {
          margin-bottom: 0;
        }
      }
    }
  }
</style>
